// TanStack Query hooks for the self-service ("My") area (spec section 2.1
// self.*). Every endpoint is row-scoped server-side to the authenticated
// user's own person_id / personnel row — there is no id parameter, so a caller
// can only ever read or edit their own data. PATCH /contact is the one write
// surface (replace-all per category). Mirrors packages/api/src/routes/self.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MyCredentialsResponse,
  MyProfileResponse,
  MyScheduleResponse,
  MyTimesheetsResponse,
  UpdateMyContactInput,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------- Query keys ----------

const KEY = 'self';

export const selfKeys = {
  all: [KEY] as const,
  profile: () => [KEY, 'profile'] as const,
  schedule: () => [KEY, 'schedule'] as const,
  timesheets: () => [KEY, 'timesheets'] as const,
  credentials: () => [KEY, 'credentials'] as const,
};

// ---------- GET /api/me/profile (self.read_profile) ----------

export function useMyProfile(enabled = true) {
  return useQuery({
    queryKey: selfKeys.profile(),
    enabled,
    queryFn: ({ signal }) => api.get<MyProfileResponse>('/api/me/profile', signal),
  });
}

// ---------- PATCH /api/me/contact (self.update_contact) ----------
//
// Replace-all of the caller's own contact categories; the fresh
// MyProfileResponse comes back so we seed the profile cache directly.

export function useUpdateMyContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMyContactInput) =>
      api.patch<MyProfileResponse>('/api/me/contact', input),
    onSuccess: (data) => {
      qc.setQueryData(selfKeys.profile(), data);
      void qc.invalidateQueries({ queryKey: selfKeys.profile() });
    },
  });
}

// ---------- GET /api/me/schedule (self.read_schedule) ----------

export function useMySchedule(enabled = true) {
  return useQuery({
    queryKey: selfKeys.schedule(),
    enabled,
    queryFn: ({ signal }) => api.get<MyScheduleResponse>('/api/me/schedule', signal),
  });
}

// ---------- GET /api/me/timesheets (self.read_timesheets) ----------

export function useMyTimesheets(enabled = true) {
  return useQuery({
    queryKey: selfKeys.timesheets(),
    enabled,
    queryFn: ({ signal }) => api.get<MyTimesheetsResponse>('/api/me/timesheets', signal),
  });
}

// ---------- GET /api/me/credentials (self.read_credentials) ----------

export function useMyCredentials(enabled = true) {
  return useQuery({
    queryKey: selfKeys.credentials(),
    enabled,
    queryFn: ({ signal }) => api.get<MyCredentialsResponse>('/api/me/credentials', signal),
  });
}
